import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV_ITEMS } from '@/constants';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const isActive = (match: string) => {
    if (match === '/') return location.pathname === '/';
    if (match.startsWith('/#')) return location.pathname === '/' && location.hash === match.slice(1);
    return location.pathname.startsWith(match);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-primary-800/60 bg-primary-900 transition-shadow ${
        scrolled ? 'shadow-[0_2px_18px_rgba(0,0,0,0.28)]' : ''
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-leaf-line text-xl" />
          </span>
          <span className="font-brand text-2xl leading-none text-background-50 md:text-[28px]">枫与铃</span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.match)
                  ? 'bg-primary-800 text-accent-300'
                  : 'text-primary-100 hover:bg-primary-800/70 hover:text-background-50'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-64 items-center md:flex lg:w-72">
          <div className="relative w-full">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-200" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视剧、资讯…"
              className="w-full rounded-full border border-primary-700 bg-primary-800/70 py-2 pl-9 pr-4 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/40"
            />
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-background-50 hover:bg-primary-800 lg:hidden"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-primary-800 bg-primary-900 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4">
            <div className="relative w-full">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-200" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视剧、资讯…"
                className="w-full rounded-full border border-primary-700 bg-primary-800/70 py-2.5 pl-9 pr-4 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-400 focus:outline-none"
              />
            </div>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(item.match)
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-primary-800/60 text-primary-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}